/**
 * 약관 문서용 작은 마크다운 그리기 (DEV-07r · D97). 라이브러리 없이 쓰는 꼴만: 제목(## ~ ####) · 문단 · 목록(- · 1.) ·
 * 들여쓴 목록 · 표(가로 스크롤) · **굵게** · [글](주소). HTML 은 넣지 않습니다(글자로만 그림).
 */
import { Fragment } from 'react';

const INLINE = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g;

function inline(text, keyPrefix) {
    return String(text)
        .split(INLINE)
        .filter((part) => part !== '')
        .map((part, index) => {
            const key = `${keyPrefix}-${index}`;
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={key}>{part.slice(2, -2)}</strong>;
            }
            const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
            if (link) {
                const external = /^https?:/.test(link[2]);
                return (
                    <a
                        key={key}
                        href={link[2]}
                        className="text-brand underline"
                        {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
                    >
                        {link[1]}
                    </a>
                );
            }
            return <Fragment key={key}>{part}</Fragment>;
        });
}

const cellsOf = (line) =>
    line
        .trim()
        .replace(/^\||\|$/g, '')
        .split('|')
        .map((cell) => cell.trim());

/** 마크다운 줄들을 덩어리(제목 · 문단 · 목록 · 표)로 */
export function blocksOf(markdown) {
    // 원본이 CRLF(Windows 에서 받은 저장소)여도 줄 끝 \r 를 떼고 봅니다. 남기면 목록 · 제목 정규식의 $ 가 \r 앞에서 걸려
    // 목록 줄이 null 이 되고 화면이 통째로 터졌습니다(/legal/terms · /legal/privacy, 2026-10-16)
    const lines = String(markdown ?? '').split(/\r?\n/);
    const blocks = [];
    let index = 0;
    while (index < lines.length) {
        const line = lines[index];
        if (!line.trim()) {
            index += 1;
            continue;
        }
        const heading = /^(#{2,4})\s+(.+)$/.exec(line);
        if (heading) {
            blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
            index += 1;
            continue;
        }
        if (/^\s*\|/.test(line)) {
            const rows = [];
            while (index < lines.length && /^\s*\|/.test(lines[index])) {
                if (!/^\s*\|[\s:|-]+\|\s*$/.test(lines[index])) {
                    rows.push(cellsOf(lines[index]));
                }
                index += 1;
            }
            blocks.push({ type: 'table', head: rows[0] ?? [], rows: rows.slice(1) });
            continue;
        }
        if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
            const items = [];
            while (index < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[index])) {
                const match = /^(\s*)([-*]|\d+\.)\s+(.*)$/.exec(lines[index]);
                items.push({
                    depth: Math.min(2, Math.floor(match[1].length / 2)),
                    ordered: /\d/.test(match[2]),
                    mark: match[2],
                    text: match[3],
                });
                index += 1;
            }
            blocks.push({ type: 'list', items });
            continue;
        }
        const paragraph = [];
        while (
            index < lines.length &&
            lines[index].trim() &&
            !/^(#{2,4}\s|\s*\||\s*([-*]|\d+\.)\s)/.test(lines[index])
        ) {
            paragraph.push(lines[index].trim());
            index += 1;
        }
        blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
    }
    return blocks;
}

const HEADING_CLASS = {
    2: 'mt-6 mb-2 text-[17px] font-bold leading-7',
    3: 'mt-4 mb-1.5 text-[15px] font-bold leading-6',
    4: 'mt-3 mb-1 text-[14px] font-semibold leading-6',
};

export function Markdown({ markdown }) {
    return (
        <div className="text-[14px] leading-[23px] text-text-primary">
            {blocksOf(markdown).map((block, blockIndex) => {
                const key = `b${blockIndex}`;
                if (block.type === 'heading') {
                    const Tag = `h${block.level}`;
                    return (
                        <Tag key={key} className={HEADING_CLASS[block.level]}>
                            {inline(block.text, key)}
                        </Tag>
                    );
                }
                if (block.type === 'table') {
                    return (
                        // 표는 가로로 밀어 봄(좁은 화면에서 판을 넘치지 않게)
                        <div key={key} className="my-3 overflow-x-auto rounded-xl border border-hairline">
                            <table className="min-w-[560px] border-collapse text-[13px] leading-5">
                                <thead className="bg-surface-sunken">
                                    <tr>
                                        {block.head.map((cell, cellIndex) => (
                                            <th key={`h${cellIndex}`} className="px-3 py-2 text-left font-semibold">
                                                {inline(cell, `${key}h${cellIndex}`)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {block.rows.map((row, rowIndex) => (
                                        <tr key={`r${rowIndex}`} className="border-t border-hairline align-top">
                                            {row.map((cell, cellIndex) => (
                                                <td key={`c${cellIndex}`} className="px-3 py-2">
                                                    {inline(cell, `${key}r${rowIndex}c${cellIndex}`)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                }
                if (block.type === 'list') {
                    return (
                        <ul key={key} className="my-2 flex list-none flex-col gap-1 p-0">
                            {block.items.map((item, itemIndex) => (
                                <li
                                    key={`i${itemIndex}`}
                                    className="flex gap-2"
                                    style={{ paddingLeft: `${item.depth * 16}px` }}
                                >
                                    <span aria-hidden="true" className="shrink-0 text-label-muted">
                                        {item.ordered ? item.mark : '·'}
                                    </span>
                                    <span>{inline(item.text, `${key}i${itemIndex}`)}</span>
                                </li>
                            ))}
                        </ul>
                    );
                }
                return (
                    <p key={key} className="my-2">
                        {inline(block.text, key)}
                    </p>
                );
            })}
        </div>
    );
}
